import { Component, type ReactElement } from "react";
import { connect } from "react-redux";

import { type IPlaybookPage } from "../../../app-utils/playbook.js";
import { type Column, type InputTableQuerySpec } from "../../../graphql.js";
import {
  clearEditData,
  fetchColumnEditingMetadata,
  loadEditingColumns,
  loadEditingData,
} from "../../../redux/modules/data-editing.js";
import { type ITableColumn } from "../../../redux/modules/table-data.js";
import { type IAppState } from "../../../redux/reducer.js";
import { ModulesIcons } from "../../../settings/assets-settings.js";
import { COLUMN_DYNAMIC_TYPES } from "../../../settings/settings.js";
import PlaybookWizard from "../PlaybookWizard.js";
import { type IWizardStep } from "../PlaybookWizardStep.js";
import PhenotypeEditingGridScreen from "./PhenotypeEditingGridScreen.js";

interface IPhenotypeEditingWizardOwnProps {
  onClose: () => void;

  // The tableSpec is passed to all downstream routines required for loading and saving the editing grid as well as to the auditing code etc.
  tableSpec: InputTableQuerySpec;
}

interface IPhenotypeEditingWizardAppStateProps {
  documentationUrl: string;
}

interface IPhenotypeEditingWizardDispatchProps {
  loadEditingColumns: (tableSpec: InputTableQuerySpec, columnsController) => Promise<Column[]>;
  fetchColumnEditingMetadata: (columns: Column[]) => Promise<Column[]>;
  loadEditingData: () => void;

  clearEditData: () => void;
}

export interface IPhenotypeEditingWizardProps
  extends IPhenotypeEditingWizardAppStateProps,
    IPhenotypeEditingWizardDispatchProps,
    IPhenotypeEditingWizardOwnProps {}

function mapStateToProps(state: IAppState): IPhenotypeEditingWizardAppStateProps {
  return {
    documentationUrl: state.config.configuration?.application?.documentationUrl,
  };
}

function mapDispatchToProps(dispatch): IPhenotypeEditingWizardDispatchProps {
  return {
    loadEditingColumns: (tableSpec: InputTableQuerySpec, columnsController) =>
      dispatch(loadEditingColumns(tableSpec, columnsController)),
    fetchColumnEditingMetadata: (columns: Column[]) => dispatch(fetchColumnEditingMetadata(columns)),
    loadEditingData: () => dispatch(loadEditingData()),

    clearEditData: () => dispatch(clearEditData()),
  };
}

const ENTER_DATA_SCREEN = "ENTER_DATA_SCREEN";

const phenotypeEditingScreens: IPlaybookPage[] = [
  {
    pageIdentifier: ENTER_DATA_SCREEN,
    title: "Enter phenotypes and values",
  },
];

/**
 * Playbook to modify phenotype values.
 */
class PhenotypeEditingWizardImpl extends Component<IPhenotypeEditingWizardProps> {
  constructor(props: IPhenotypeEditingWizardProps) {
    super(props);
  }

  componentDidMount() {
    this.loadDataColumnsAndRows();
  }

  componentWillUnmount() {
    this.props.clearEditData();
  }

  onClose = () => {
    this.props.onClose();
  };

  setColumnEditability = (columns: ITableColumn[]) => {
    return columns
      .map((column) => {
        const editable: boolean = column.dynamicType === COLUMN_DYNAMIC_TYPES.ORGANISM_PROPERTY;
        return {
          ...column,
          editable: editable,
          hidden: !editable && !column.fixed,
        };
      })
      .filter((c) => !c.hidden);
  };

  loadDataColumnsAndRows = () => {
    this.props
      .loadEditingColumns(this.props.tableSpec, this.setColumnEditability)
      .then((columns) => this.props.fetchColumnEditingMetadata(columns.filter((col: ITableColumn) => col.editable)))
      .then(this.props.loadEditingData);
  };

  render(): ReactElement {
    return (
      <PlaybookWizard
        title={"Data Entry"}
        steps={phenotypeEditingScreens}
        mapStepToScreen={this.mapStepToScreen}
        needsWideSpace={() => true}
        onClose={this.onClose}
        icon={ModulesIcons.DEFAULT}
        helpLink={`${this.props.documentationUrl}Edit%2bPhenotype%2bAssignments`}
      />
    );
  }

  mapStepToScreen = (page: IPlaybookPage): IWizardStep => {
    switch (page.pageIdentifier) {
      case ENTER_DATA_SCREEN:
        return <PhenotypeEditingGridScreen playbookPage={page} />;
    }
  };
}

const PhenotypeEditingWizard = connect<
  IPhenotypeEditingWizardAppStateProps,
  IPhenotypeEditingWizardDispatchProps,
  IPhenotypeEditingWizardOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(PhenotypeEditingWizardImpl);

export { PhenotypeEditingWizard };
export default PhenotypeEditingWizard;
